import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { getApiClient } from "../api-client";

// ─── Types ───────────────────────────────────────────────────────────────

export type ApiVotingPhase = "CLOSED" | "NOMINATING" | "RANKING" | "REVEAL";

/** Phase shown by the state-machine UI. Maps from API phase. */
export type UiVotingPhase = "SETUP" | "NOMINATING" | "RANKING" | "CLOSED";

export interface VotingSessionBook {
  id: string;
  title: string;
  author: string | null;
  coverUrl: string | null;
}

export interface VotingSessionNomination {
  id: string;
  bookId: string;
  userId: string;
  book: VotingSessionBook;
  user: {
    id: string;
    displayName: string | null;
    image: string | null;
  };
}

export interface VotingSessionRanking {
  id: string;
  bookId: string;
  rank: number;
  book: VotingSessionBook;
}

export interface VotingSession {
  id: string;
  bookClubId: string;
  phase: ApiVotingPhase;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  winnerBookId: string | null;
  winnerBook: VotingSessionBook | null;
  createdBy: { id: string; displayName: string | null; image: string | null };
  nominations: VotingSessionNomination[];
  userNomination: VotingSessionNomination | null;
  userRankings: VotingSessionRanking[];
  nominationCount: number;
  rankingCount: number;
  memberCount: number;
}

// ─── Query keys ──────────────────────────────────────────────────────────

export const votingQueryKey = (clubId: string) =>
  ["clubs", clubId, "voting"] as const;

// ─── Phase mapping ───────────────────────────────────────────────────────

export function apiPhaseToUiPhase(
  phase: ApiVotingPhase | null | undefined,
): UiVotingPhase {
  if (!phase) return "SETUP";
  if (phase === "CLOSED") return "SETUP";
  if (phase === "NOMINATING") return "NOMINATING";
  if (phase === "RANKING") return "RANKING";
  return "CLOSED";
}

/** The next API phase to advance to from a given API phase, or null if terminal. */
export function nextApiPhase(
  phase: ApiVotingPhase,
): "NOMINATING" | "RANKING" | "REVEAL" | null {
  if (phase === "CLOSED") return "NOMINATING";
  if (phase === "NOMINATING") return "RANKING";
  if (phase === "RANKING") return "REVEAL";
  return null;
}

// ─── Queries ─────────────────────────────────────────────────────────────

export function useVotingSession(clubId: string | undefined) {
  return useQuery<VotingSession | null>({
    queryKey: clubId ? votingQueryKey(clubId) : ["clubs", "_none", "voting"],
    enabled: Boolean(clubId),
    queryFn: () =>
      getApiClient().get<VotingSession | null>(`/clubs/${clubId}/voting`),
    staleTime: 10 * 1000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

export function useCreateVotingSession(clubId: string) {
  const qc = useQueryClient();
  return useMutation<VotingSession, Error, void>({
    mutationFn: () =>
      getApiClient().post<VotingSession>(`/clubs/${clubId}/voting`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: votingQueryKey(clubId) });
    },
  });
}

export function useDeleteVotingSession(clubId: string) {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, void>({
    mutationFn: () =>
      getApiClient().delete<{ success: boolean }>(`/clubs/${clubId}/voting`),
    onSuccess: () => {
      qc.setQueryData(votingQueryKey(clubId), null);
      qc.invalidateQueries({ queryKey: votingQueryKey(clubId) });
    },
  });
}

// ─── Phase-advance hook ──────────────────────────────────────────────────

interface PhaseAdvanceContext {
  previousPhase: ApiVotingPhase;
  previousSession: VotingSession | null | undefined;
}

interface UseVotingPhaseAdvanceArgs {
  clubId: string;
  sessionId: string | null | undefined;
  currentPhase: ApiVotingPhase | null | undefined;
}

export interface UseVotingPhaseAdvanceResult {
  phase: UiVotingPhase;
  apiPhase: ApiVotingPhase | null;
  advance: () => void;
  isAdvancing: boolean;
  error: Error | null;
  canAdvance: boolean;
}

/**
 * Advance the voting phase for a session. Optimistically writes the next phase
 * to the query cache; reconciles when the WS `voting:phase_changed` event
 * lands and the route invalidates the query.
 *
 * Concurrent advances from `OWNER` + `ADMIN` are made safe on the server
 * (optimistic-lock guard returns 409) — the loser of the race surfaces the
 * 409 via `error`, and `onError` rolls back the cache to the prior phase.
 */
export function useVotingPhaseAdvance({
  clubId,
  sessionId,
  currentPhase,
}: UseVotingPhaseAdvanceArgs): UseVotingPhaseAdvanceResult {
  const qc = useQueryClient();
  const key = votingQueryKey(clubId);

  const target = currentPhase ? nextApiPhase(currentPhase) : null;

  const mutation = useMutation<
    VotingSession,
    Error,
    "NOMINATING" | "RANKING" | "REVEAL",
    PhaseAdvanceContext
  >({
    mutationFn: (phase) =>
      getApiClient().patch<VotingSession>(`/clubs/${clubId}/voting`, { phase }),
    onMutate: async (phase) => {
      await qc.cancelQueries({ queryKey: key });
      const previousSession = qc.getQueryData<VotingSession | null>(key);
      const previousPhase = previousSession?.phase ?? "CLOSED";
      if (previousSession) {
        qc.setQueryData<VotingSession | null>(key, {
          ...previousSession,
          phase,
        });
      }
      return { previousPhase, previousSession };
    },
    onError: (_err, _phase, ctx) => {
      if (ctx?.previousSession !== undefined) {
        qc.setQueryData<VotingSession | null>(key, ctx.previousSession);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });

  const advance = useCallback(() => {
    if (!sessionId || !target) return;
    mutation.mutate(target);
  }, [sessionId, target, mutation]);

  return useMemo(
    () => ({
      phase: apiPhaseToUiPhase(currentPhase),
      apiPhase: currentPhase ?? null,
      advance,
      isAdvancing: mutation.isPending,
      error: mutation.error ?? null,
      canAdvance: Boolean(sessionId && target),
    }),
    [
      currentPhase,
      advance,
      mutation.isPending,
      mutation.error,
      sessionId,
      target,
    ],
  );
}

// ─── WS reconciliation helper ────────────────────────────────────────────

/**
 * Apply a WS-delivered phase change to the cache. The route subscribes to
 * `useClubChannel` and calls this when `voting:phase_changed` arrives.
 */
export function applyVotingPhaseChange(
  qc: QueryClient,
  clubId: string,
  payload: {
    sessionId: string;
    phase: string;
    winnerBookId: string | null;
  },
): void {
  const key = votingQueryKey(clubId);
  const current = qc.getQueryData<VotingSession | null>(key);
  if (current && current.id === payload.sessionId) {
    qc.setQueryData<VotingSession | null>(key, {
      ...current,
      phase: payload.phase as ApiVotingPhase,
      winnerBookId: payload.winnerBookId,
    });
  }
  qc.invalidateQueries({ queryKey: key });
}
